import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, AlertTriangle } from 'lucide-react';
import { TransactionTable } from '../../../../components/import/transaction-table';
import { TransactionImportSuccess } from '../../../../components/import/transaction-import-success';
import { assetImportService } from '../../../../services/asset-import.service';
import { getAuthConfig } from '@/lib/auth-fetch';
import { ImportStatus } from '../../../../types/asset-import.types';
import type {
  AccountingConnection,
  Transaction,
  TransactionFilterOptions,
} from '../../../../types/asset-import.types';
import { convertTransactionEditsToOverrides } from './utils/convert-transaction-edits';
import { useAgentProcessing } from '@/hooks/use-agent-processing';
import { AgentSessionStatus, isAgentProcessingInProgress } from '@/types/agent.types';

const AGENT_FAILED_MESSAGE_DURATION_MS = 3000;
const SKIP_OPTIMISATION_DELAY_MS = 5000;

export default function TransactionScan() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<ImportStatus>(ImportStatus.Processing);
  const [connection, setConnection] = useState<AccountingConnection | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);
  const [importedCount, setImportedCount] = useState(0);
  const [importedTransactionIds, setImportedTransactionIds] = useState<string[]>([]);
  const [loadingStep, setLoadingStep] = useState<'connecting' | 'scanning' | 'analysing' | 'agent-failed' | 'importing'>('connecting');
  const [showSkipButton, setShowSkipButton] = useState(false);

  // Ref to prevent duplicate fetches (e.g., from React StrictMode or component remounting)
  const hasFetchedRef = useRef(false);
  
  // Ref to track when analysing step started for skip button timing
  const analysingStartTimeRef = useRef<number | null>(null);

  // Get filters and agent processing flag from navigation state
  const filters = location.state?.filters as TransactionFilterOptions | undefined;
  const enableAgentProcessing = location.state?.enableAgentProcessing as boolean | undefined;

  // Agent processing hook
  const {
    processedTransactions,
    startPolling: startAgentPolling,
    cancelSession: cancelAgentSession,
  } = useAgentProcessing({
    onComplete: () => {
      // Agent processing complete, show the transactions table
      setStatus(ImportStatus.Validating);
    },
    onError: (error) => {
      console.error('Agent processing failed:', error);
      // Fall back to showing transactions without agent suggestions
      setStatus(ImportStatus.Validating);
    },
  });

  // Merge agent suggestions into transactions when available
  const transactionsWithSuggestions = transactions.map(tx => {
    const processed = processedTransactions.find(p => p.transactionId === tx.id);
    if (processed && processed.suggestions.length > 0) {
      // Apply category suggestion if available
      const categorySuggestion = processed.suggestions.find(s => s.fieldName === 'category');
      if (categorySuggestion) {
        return {
          ...tx,
          category: categorySuggestion.suggestedValue,
          agentSuggestion: {
            originalCategory: categorySuggestion.originalValue,
            suggestedCategory: categorySuggestion.suggestedValue,
            confidence: categorySuggestion.confidence,
            reason: categorySuggestion.reason,
          },
        };
      }
    }
    return tx;
  });

  useEffect(() => {
    // Prevent duplicate fetches (e.g., from React StrictMode or component remounting)
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    loadConnectionAndFetch();
  }, []);

  // Effect to show skip button after 5 seconds of analysing
  useEffect(() => {
    if (loadingStep === 'analysing') {
      analysingStartTimeRef.current = Date.now();
      const timer = setTimeout(() => {
        setShowSkipButton(true);
      }, SKIP_OPTIMISATION_DELAY_MS);
      return () => clearTimeout(timer);
    } else {
      analysingStartTimeRef.current = null;
      setShowSkipButton(false);
    }
  }, [loadingStep]);

  // Handler to skip optimisation and proceed with original data
  const handleSkipOptimisation = (): void => {
    cancelAgentSession();
    setStatus(ImportStatus.Validating);
  };

  // Get the financials instance ID from auth config
  const getFinancialsInstanceId = (): string => {
    const authConfig = getAuthConfig();
    return authConfig.financialsInstanceId ?? authConfig.instanceId ?? '';
  };

  const loadConnectionAndFetch = async () => {
    try {
      const financialsInstanceId = getFinancialsInstanceId();
      if (!financialsInstanceId) {
        console.error('No Financials instance ID configured');
        setStatus(ImportStatus.Error);
        return;
      }

      const conn = await assetImportService.getAccountingConnection();
      setConnection(conn);

      // Start fetching immediately with filters from navigation state
      await handleFetchTransactions(filters ?? {
        dateFrom: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
        dateTo: new Date()
      });
    } catch (error) {
      console.error('Failed to load connection:', error);
      setStatus(ImportStatus.Error);
    }
  };

  const handleFetchTransactions = async (filterOptions: TransactionFilterOptions) => {
    try {
      setStatus(ImportStatus.Processing);
      setLoadingStep('scanning');

      const result = await assetImportService.fetchTransactions(
        filterOptions,
        enableAgentProcessing ?? false
      );

      setTransactions(result.transactions);

      // Auto-select eligible transactions
      const eligibleIds = result.transactions
        .filter((tx) => tx.status === 'eligible' && tx.canCreateAsset)
        .map((tx) => tx.id);
      setSelectedTransactionIds(eligibleIds);

      // If agent processing is enabled and we have a session ID, check the initial status
      if (enableAgentProcessing && result.agentSessionId) {
        // Check if agent already failed (e.g., Forbidden response from agent API)
        // The backend returns numeric status values, so we need to handle both string and number
        const agentStatus = result.agentStatus;
        // Convert to string/number agnostic comparison using the raw value
        const statusValue = agentStatus as unknown;
        const isAgentFailed = statusValue !== undefined && (
          statusValue === AgentSessionStatus.Failed ||
          statusValue === AgentSessionStatus.Cancelled ||
          statusValue === AgentSessionStatus.TimedOut ||
          statusValue === 4 || // Failed (numeric from backend)
          statusValue === 5 || // Cancelled (numeric from backend)
          statusValue === 6    // TimedOut (numeric from backend)
        );

        if (isAgentFailed) {
          // Agent already failed, show message briefly before going to validation
          console.warn('Agent processing failed, proceeding without AI suggestions');
          setLoadingStep('agent-failed');
          setTimeout(() => {
            setStatus(ImportStatus.Validating);
          }, AGENT_FAILED_MESSAGE_DURATION_MS);
        } else if (agentStatus !== undefined && isAgentProcessingInProgress(agentStatus)) {
          // Agent is still processing, start polling
          setLoadingStep('analysing');
          startAgentPolling(result.agentSessionId);
          // Status will be updated by the agent processing hook callbacks
        } else {
          // Unknown status or already completed, go to validation
          setStatus(ImportStatus.Validating);
        }
      } else {
        // No agent processing, go directly to validation
        setStatus(ImportStatus.Validating);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setStatus(ImportStatus.Error);
    }
  };

  const handleCreateAssets = async (
    useGrossValue: boolean,
    transactionEdits: Map<string, Partial<Transaction>>
  ): Promise<void> => {
    try {
      setStatus(ImportStatus.Processing);
      setLoadingStep('importing');

      // Convert Map to Record for the API call
      const transactionOverrides = convertTransactionEditsToOverrides(transactionEdits);

      const result = await assetImportService.createAssetsFromTransactions(
        selectedTransactionIds,
        useGrossValue,
        Object.keys(transactionOverrides).length > 0 ? transactionOverrides : undefined
      );

      if (result.success) {
        setImportedCount(result.importedCount);
        setImportedTransactionIds(selectedTransactionIds);
        setStatus(ImportStatus.Complete);
      } else {
        setStatus(ImportStatus.Error);
      }
    } catch (error) {
      console.error('Asset creation failed:', error);
      setStatus(ImportStatus.Error);
    }
  };

  const handleReset = () => {
    setStatus(ImportStatus.Idle);
    setTransactions([]);
    setSelectedTransactionIds([]);
    setImportedCount(0);
  };

  // Helper function to get the loading message based on current step
  const getLoadingMessage = (): string => {
    if (loadingStep === 'agent-failed') {
      return 'AI optimisation is unavailable. Proceeding with original data...';
    }
    if (loadingStep === 'importing') {
      return 'Creating assets...';
    }
    if (loadingStep === 'analysing') {
      return 'AI is optimising your import...';
    }
    return `Scanning transactions from ${connection?.systemName ?? 'accounting system'}...`;
  };

  // Helper function to get the dot indicator class for each loading step
  const getStepDotClass = (step: 'connecting' | 'scanning' | 'analysing'): string => {
    if (loadingStep === step) {
      return 'bg-violet-500 animate-pulse';
    }
    if (step === 'scanning' && loadingStep === 'analysing') {
      return 'bg-violet-500';
    }
    if (step === 'connecting') {
      return 'bg-violet-500';
    }
    return 'bg-slate-300 dark:bg-slate-600';
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/assets/import')}
              className="h-8 w-8"
            >
              <Search className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight">Scan Transactions For Assets</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {connection ? `Review and create assets from transactions in ${connection.systemName}` : 'Analyse transactions to discover and create new assets'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Content based on status */}
        {status === ImportStatus.Processing && (
          <div className="flex items-center justify-center min-h-[calc(100vh-200px)] p-6">
            <div className="bg-white dark:bg-black rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-800/50 p-12 max-w-md w-full">
              <div className="text-center space-y-6">
                {loadingStep === 'agent-failed' ? (
                  <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto" />
                ) : (
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-violet-500 border-t-transparent mx-auto" />
                )}
                <p className="text-xl font-semibold text-slate-800 dark:text-slate-200">
                  {getLoadingMessage()}
                </p>
                {loadingStep !== 'agent-failed' && loadingStep !== 'importing' && (
                  <div className="flex justify-center gap-8 pt-4">
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${getStepDotClass('connecting')}`} />
                      <span className="text-sm text-slate-600 dark:text-slate-400">Connecting</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${getStepDotClass('scanning')}`} />
                      <span className={`text-sm ${loadingStep === 'scanning' || loadingStep === 'analysing'
                          ? 'text-slate-600 dark:text-slate-400'
                          : 'text-slate-400'
                        }`}>Scanning</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${getStepDotClass('analysing')}`} />
                      <span className={`text-sm ${loadingStep === 'analysing' ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400'
                        }`}>Analysing</span>
                    </div>
                  </div>
                )}
                {loadingStep !== 'agent-failed' && loadingStep !== 'importing' && (
                  <div className="flex justify-center gap-3 mt-6">
                    <Button variant="outline" onClick={() => {
                      if (loadingStep === 'analysing') {
                        cancelAgentSession();
                      }
                      navigate('/assets/import');
                    }}>
                      Cancel
                    </Button>
                    {showSkipButton && loadingStep === 'analysing' && (
                      <Button variant="secondary" onClick={handleSkipOptimisation}>
                        Skip Optimisation
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {status === ImportStatus.Validating && (
          <TransactionTable
            transactions={transactionsWithSuggestions}
            selectedTransactionIds={selectedTransactionIds}
            onSelectionChange={setSelectedTransactionIds}
            onCreateAssets={handleCreateAssets}
            onCancel={() => navigate('/assets/import')}
          />
        )}

        {status === ImportStatus.Complete && connection && (
          <div className="flex items-center justify-center min-h-[calc(100vh-200px)] p-6">
            <TransactionImportSuccess
              count={importedCount}
              systemName={connection.systemName}
              onViewAssets={() => navigate('/assets', {
                state: {
                  filterByImportedIds: importedTransactionIds
                }
              })}
              importedTransactionIds={importedTransactionIds}
            />
          </div>
        )}

        {status === ImportStatus.Error && (
          <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
            <div className="text-center">
              <p className="text-lg font-medium text-destructive">
                An error occurred during the scan
              </p>
              <Button onClick={handleReset} className="mt-4">
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
